// Catalog.ID Client — account chat-root publication
// ============================================================================
// The once-per-ACCOUNT ceremony that publishes this account's `chat_sign` ROOT
// public half on the `registry_signing_key` claim track under key class
// `chat_root` (chat migration §3 "`chat_sign` publication", §14 M3).
//
// WHY IT EXISTS AT ALL. A chat message is verified as a chain: envelope
// signature by a per-machine sub-key → the sub-key's cert → the account root's
// validity window on the claim track (§3). An account whose root was never
// published cannot be verified by ANYBODY — chat is unusable from first launch
// without this. Until M3 the only way to get a root published was
// `apps/cid/dev/chat-cert.ts publish-root`, a dev-stack-only path whose own
// header calls itself a stand-in for this flow.
//
// WHAT IS *NOT* OWED HERE: key generation. `chat_sign` is generated into every
// bag at registration (registration wizard, delegate-activation wizard, org
// wizard) as a BAG-ONLY key — `BAG_ONLY_KEY_IDS` in `keybag.ts`, skip-listed
// server-side so no `accountpublickey` row is demanded. Only publication is
// owed, and this module is the whole of it.
//
// THE THREE DELTAS vs the `agents-list-pane` pipeline this clones (§14 M3):
//   1. the validator challenge is signed with `chat_sign`, NEVER
//      `main_signing_key`. Domain separation is the entire point of a separate
//      chat key; a root proven with the main key would also be a main-key
//      publication in every way that matters.
//   2. `keyClass: "chat_root"` is declared at publish. Without it the same
//      1984-byte hybrid blob publishes as `machine` and silently gains EFS
//      retrieval / wrap-signing authority and OIDC machine-key binding
//      (`PublicClaimKeyClasses` says this at length). The class is immutable
//      after publish — there is no edit path — so getting it right HERE is the
//      only chance.
//   3. the claim key is re-wrapped for every operator, because publication runs
//      the standard pipeline and `operator-review-pane.ts` has to decrypt the
//      value to approve it. That pane is class-blind and needs no changes.
//
// WHY THE TRANSPORT IS INJECTED. Everything here is DOM-free and imports no
// `api/*` module, so the exact code the browser ceremony runs is also
// importable from Node — the `dev/ceremony-sign.ts` rule: a probe must prove
// the CLIENT's pipeline, not a second implementation of it.
// `apps/chat/dev/probes/m3-root-*` drives these functions through a
// `DevApiClient`-backed adapter; `pane-types/chat-root-pane.ts` drives them
// through the browser `api/*` modules.
//
// THE §17 WIRE-FORM TRAP. `signHybrid` returns a JSON wrapper of the two
// signature halves and every DURABLE Catalog record carries the concatenated
// 3373-byte form. The ownership-challenge endpoint is the documented
// exception: `RegistryKeyVerifyController` takes "the canonical-JSON
// SignatureWrapper". Nothing durable is signed here — the durable artifact is
// the claim's public VALUE (the root public key) — so `wrapperToJson` is
// correct on this one hop and nowhere else.
// ============================================================================

import * as aes from './aes';
import { encryptClaim, reEncryptClaimKey } from './claims';
import { decryptKeyBag, getKeyBagEntry, type KeyBagServerResponse } from './keybag';
import {
    computeHybridSigningKeyFingerprint, signHybrid, signingPublicKeyFromSeed,
    wrapperToJson, HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_SEED_LEN,
} from './signing';

/** Keybag slot for the account-level chat signing root (migration §3). */
export const CHAT_SIGN_KEY_ID = 'chat_sign';

/** Keybag + `accountpublickey` slot the claim key is wrapped to. */
export const ATTR_ENCRYPT_KEY_ID = 'attr_encrypt';

/** The claim track chat roots publish on — shared with per-machine keys, which
 *  is exactly why the class discriminator below exists. */
export const REGISTRY_SIGNING_KEY_CLAIM_TYPE = 'registry_signing_key';

/** Mirrors `CatalogId.Server.PublicClaimKeyClasses.ChatRoot` and
 *  `apps/cid/dev/seed/chat-root.ts`'s `CHAT_ROOT_KEY_CLASS`. */
export const CHAT_ROOT_KEY_CLASS = 'chat_root';

/** Default claim label. Encrypted client-side like any other claim label. */
const DEFAULT_LABEL = 'Chat account root';

// ── transport ───────────────────────────────────────────────────────────

/** One `/api/publicclaims` row, as much of it as this module reads. */
export interface PublicClaimRowLike {
    publicClaimId: number;
    claimUuid: string;
    claimType: string;
    /** Empty/absent while pending — the operator supplies it at approve time. */
    publicValue: string | null;
    status: string;
    rejectionReason?: string | null;
    publishedAt?: string | null;
    /** Absent only against a server predating the key-class column. */
    keyClass?: string;
}

export interface ChatRootTransport {
    getKeyBag(): Promise<KeyBagServerResponse>;
    getOwnPublicKeys(): Promise<{ keyId: string; publicKey: string }[]>;
    listPublicClaims(): Promise<PublicClaimRowLike[]>;
    createClaim(req: {
        claimType: string;
        encryptedValue: string;
        nonce: string;
        encryptedKey: string;
        encryptedLabel?: string;
        labelNonce?: string;
    }): Promise<{ claimUuid: string }>;
    getValidatorKey(): Promise<{ publicKey: string }>;
    validateClaim(claimUuid: string, req: {
        validationType: string;
        encryptedKey: string;
    }): Promise<{ challenge?: string }>;
    verifyRegistryKey(
        challenge: string, signature: string, publicKey: string,
    ): Promise<{ success: boolean }>;
    cancelValidation(claimUuid: string): Promise<void>;
    listOperators(): Promise<{ cid: string; publicKey: string }[]>;
    publishClaim(claimUuid: string, req: {
        operatorKeys: { operatorCid: string; encryptedKey: string }[];
        keyClass: string;
    }): Promise<void>;
}

// ── state ───────────────────────────────────────────────────────────────

export type ChatRootStatus = 'none' | 'pending' | 'published' | 'rejected';

export interface ChatRootState {
    status: ChatRootStatus;
    publicClaimId?: number;
    claimUuid?: string;
    /** base64 of the 1984-byte root public key; '' while pending. */
    publicValueBase64?: string | null;
    /** 128 lowercase hex SHA-512 of the published key, when there is one. */
    fingerprint?: string;
    publishedAt?: string | null;
    rejectionReason?: string | null;
}

export class ChatRootPublicationError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'ChatRootPublicationError';
        this.code = code;
    }
}

/**
 * The account's chat-root publication state, read from `/api/publicclaims`
 * ALONE — no keybag, no supersecret. That is deliberate: the pane must be able
 * to render "published / awaiting review / not published" for a locked session,
 * and a first-run trigger must not be gated on an unlock.
 *
 * Precedence published > pending > rejected: a rejected row left over from an
 * earlier attempt must not mask a live publication (the server keeps at most
 * one pending and one published row PER CLAIM, but an account may hold several
 * chat-root claims across retries).
 */
export async function readChatRootState(
    transport: ChatRootTransport,
): Promise<ChatRootState> {
    const rows = (await transport.listPublicClaims()).filter(
        r => r.claimType === REGISTRY_SIGNING_KEY_CLAIM_TYPE
            && r.keyClass === CHAT_ROOT_KEY_CLASS);

    for (const status of ['published', 'pending', 'rejected'] as const) {
        const row = rows.find(r => r.status === status);
        if (!row) continue;
        const state: ChatRootState = {
            status,
            publicClaimId: row.publicClaimId,
            claimUuid: row.claimUuid,
            publicValueBase64: row.publicValue,
            publishedAt: row.publishedAt ?? null,
            rejectionReason: row.rejectionReason ?? null,
        };
        const fingerprint = await fingerprintOfPublicValue(row.publicValue);
        if (fingerprint) state.fingerprint = fingerprint;
        return state;
    }
    return { status: 'none' };
}

/**
 * 128-hex SHA-512 of a published key's raw bytes, or null when the row carries
 * no value (pending) or something that is not a hybrid public key. Never
 * throws: a malformed row must degrade to "no fingerprint shown", not to a
 * pane that fails to render.
 */
export async function fingerprintOfPublicValue(
    publicValue: string | null | undefined,
): Promise<string | null> {
    if (!publicValue) return null;
    try {
        const bytes = aes.fromBase64(publicValue);
        if (bytes.length !== HYBRID_PUBLIC_KEY_LEN) return null;
        return aes.toHex(await computeHybridSigningKeyFingerprint(bytes));
    } catch {
        return null;
    }
}

// ── publication ─────────────────────────────────────────────────────────

export interface PublishChatRootOptions {
    /** The account supersecret. This ceremony is gated on an explicit unlock
     *  (§3 custody doctrine), not on an ambient session. */
    supersecret: string;
    /** Claim label; encrypted under the claim's own key like any other. */
    label?: string;
}

export interface ChatRootPublishResult {
    /** Null when nothing was created (`alreadyPublished`). */
    claimUuid: string | null;
    /** 128 lowercase hex SHA-512 of the root public key. */
    fingerprint: string;
    /** base64 of the 1984-byte root public key. */
    publicKeyBase64: string;
    /** True when an identical root was already published or already pending. */
    alreadyPublished: boolean;
    /** State as of the end of the call. */
    state: ChatRootState;
    /** Non-fatal condition worth showing the user (e.g. no operator yet). */
    warning?: string;
}

/**
 * Run the ceremony: unlock the bag with `supersecret`, read the `chat_sign`
 * root, and drive claim → validator challenge → ownership proof → publish
 * (`keyClass: chat_root`, operator keys re-wrapped).
 *
 * IDEMPOTENT by construction. The dev DB accumulates, a user can double-click,
 * and two tabs can race; a second root on the track would leave "which one is
 * current" undecidable for every verifier. So an existing PUBLISHED row with
 * this exact public value, or any PENDING row, short-circuits to a no-op. A
 * published row with a DIFFERENT value is a hard refusal, not a second
 * publication: it means the bag and the track disagree about this account's
 * root, and quietly publishing a rival root is the worst possible answer.
 *
 * The ceremony stops at "pending operator review". It deliberately does NOT
 * self-approve when the session happens to be an operator
 * (`registry-handlers.ts` does that for machine keys): the account root is the
 * one artifact whose review should be somebody else's decision, and the probe
 * seeds a real operator approve rather than a shortcut.
 */
export async function publishChatRoot(
    transport: ChatRootTransport,
    opts: PublishChatRootOptions,
): Promise<ChatRootPublishResult> {
    // ── 1. unlock ───────────────────────────────────────────────────────
    // Before anything is created: a wrong supersecret must cost nothing.
    let bagResponse: KeyBagServerResponse;
    try {
        bagResponse = await transport.getKeyBag();
    } catch (err) {
        throw new ChatRootPublicationError('keybag-unavailable',
            `Could not load your key bag: ${messageOf(err)}`);
    }
    let bag;
    try {
        bag = await decryptKeyBag(bagResponse, opts.supersecret);
    } catch {
        throw new ChatRootPublicationError('bad-supersecret',
            'Invalid supersecret — could not decrypt your key bag.');
    }

    const signEntry = getKeyBagEntry(bag, CHAT_SIGN_KEY_ID);
    if (!signEntry) {
        // Never substitute another key. A root minted or borrowed here would
        // produce certs that chain to nothing on the claim track, and signing
        // chat material with `main_signing_key` violates §3 outright.
        throw new ChatRootPublicationError('chat-sign-root-not-in-bag',
            'This account has no chat_sign root in its key bag. Accounts registered '
            + 'before chat keys existed do not have one, and it cannot be substituted '
            + 'or minted here — chat_sign domain separation is mandatory. Rotate your '
            + 'keys or re-register on a build that generates chat keys.');
    }
    const attrEntry = getKeyBagEntry(bag, ATTR_ENCRYPT_KEY_ID);
    if (!attrEntry) {
        throw new ChatRootPublicationError('attr-encrypt-not-in-bag',
            'This account has no attr_encrypt key in its key bag, so the claim key '
            + 'cannot be wrapped for the validator or the operators.');
    }

    const rootSeed = decodeOrThrow(signEntry.privateKey, 'chat-sign-root-malformed',
        'The chat_sign entry in your key bag is not decodable.');
    if (rootSeed.length !== HYBRID_SECRET_SEED_LEN) {
        rootSeed.fill(0);
        throw new ChatRootPublicationError('chat-sign-root-malformed',
            `The chat_sign entry is ${rootSeed.length} bytes; a chat root must be `
            + `exactly ${HYBRID_SECRET_SEED_LEN} (ed25519_seed(32) || mldsa65_seed(32)). `
            + 'A non-hybrid root is refused by every verifier.');
    }
    const attrSecret = decodeOrThrow(attrEntry.privateKey, 'attr-encrypt-malformed',
        'The attr_encrypt entry in your key bag is not decodable.');

    try {
        const rootPublicKey = signingPublicKeyFromSeed(rootSeed);
        const publicKeyBase64 = aes.toBase64(rootPublicKey);
        const fingerprint = aes.toHex(
            await computeHybridSigningKeyFingerprint(rootPublicKey));

        // ── 2. idempotency ──────────────────────────────────────────────
        const existing = await readChatRootState(transport);
        if (existing.status === 'published') {
            if (existing.publicValueBase64 !== publicKeyBase64) {
                throw new ChatRootPublicationError('published-root-mismatch',
                    'A DIFFERENT chat root is already published for this account. '
                    + 'Publishing a second one would leave every verifier unable to '
                    + 'decide which root is current. Revoke the published one first if '
                    + 'this key bag really holds the successor root.');
            }
            return {
                claimUuid: existing.claimUuid ?? null, fingerprint, publicKeyBase64,
                alreadyPublished: true, state: existing,
            };
        }
        if (existing.status === 'pending') {
            return {
                claimUuid: existing.claimUuid ?? null, fingerprint, publicKeyBase64,
                alreadyPublished: true, state: existing,
            };
        }

        // ── 3. the claim ────────────────────────────────────────────────
        const attrPublicKeyB64 = (await transport.getOwnPublicKeys())
            .find(k => k.keyId === ATTR_ENCRYPT_KEY_ID)?.publicKey;
        if (!attrPublicKeyB64) {
            throw new ChatRootPublicationError('no-attr-encrypt-public-key',
                'This account has no published attr_encrypt public key.');
        }
        const encrypted = await encryptClaim(
            publicKeyBase64, aes.fromBase64(attrPublicKeyB64), opts.label ?? DEFAULT_LABEL);
        const { claimUuid } = await transport.createClaim({
            claimType: REGISTRY_SIGNING_KEY_CLAIM_TYPE,
            encryptedValue: encrypted.encryptedValue,
            nonce: encrypted.nonce,
            encryptedKey: encrypted.encryptedKey,
            ...(encrypted.encryptedLabel ? { encryptedLabel: encrypted.encryptedLabel } : {}),
            ...(encrypted.labelNonce ? { labelNonce: encrypted.labelNonce } : {}),
        });

        // ── 4. validator challenge ──────────────────────────────────────
        // The validator gets its own copy of the claim key so it can read the
        // value it is being asked to certify.
        const { publicKey: validatorKemB64 } = await transport.getValidatorKey();
        const validatorEncryptedKey = await reEncryptClaimKey(
            encrypted.encryptedKey, attrSecret, aes.fromBase64(validatorKemB64));
        const validateResp = await transport.validateClaim(claimUuid, {
            validationType: REGISTRY_SIGNING_KEY_CLAIM_TYPE,
            encryptedKey: validatorEncryptedKey,
        });
        if (!validateResp.challenge) {
            throw new ChatRootPublicationError('validate-no-challenge',
                'The validator did not return an ownership challenge.');
        }

        // ── 5. sign it WITH THE CHAT ROOT ───────────────────────────────
        // Delta 1. Never main_signing_key.
        const wrapper = signHybrid(
            new TextEncoder().encode(validateResp.challenge), rootSeed);
        const verifyResp = await transport.verifyRegistryKey(
            validateResp.challenge, wrapperToJson(wrapper), publicKeyBase64);
        if (!verifyResp.success) {
            // Leave no orphan stuck in "pending validation" — the same
            // best-effort cleanup `registry-handlers.ts` does.
            try { await transport.cancelValidation(claimUuid); } catch { /* best effort */ }
            throw new ChatRootPublicationError('verify-rejected',
                'The validator rejected the chat-root ownership proof.');
        }

        // ── 6. publish with the class + operator keys ───────────────────
        // Deltas 2 and 3.
        let operators: { cid: string; publicKey: string }[] = [];
        try {
            operators = await transport.listOperators();
        } catch {
            operators = [];
        }
        const operatorKeys: { operatorCid: string; encryptedKey: string }[] = [];
        for (const op of operators) {
            operatorKeys.push({
                operatorCid: op.cid,
                encryptedKey: await reEncryptClaimKey(
                    encrypted.encryptedKey, attrSecret, aes.fromBase64(op.publicKey)),
            });
        }
        await transport.publishClaim(claimUuid, {
            operatorKeys, keyClass: CHAT_ROOT_KEY_CLASS,
        });

        const state = await readChatRootState(transport);
        const result: ChatRootPublishResult = {
            claimUuid, fingerprint, publicKeyBase64, alreadyPublished: false, state,
        };
        if (operators.length === 0) {
            // Not fatal, and not silent either: the claim is validated and the
            // publication request exists, but nobody can approve it yet. The
            // server allows this (it only demands operator keys when operators
            // exist), so the honest outcome is a published request that stays
            // pending until an operator↔org binding is in place.
            result.warning = 'Published, but no operator is available to review it yet — '
                + 'the request will stay pending until one exists.';
        }
        return result;
    } finally {
        // §3 custody doctrine: the master is zeroized within seconds of use.
        // The bag's own base64 string is untouched; the entry stays usable.
        rootSeed.fill(0);
        attrSecret.fill(0);
    }
}

function decodeOrThrow(base64: string, code: string, message: string): Uint8Array {
    try {
        return aes.fromBase64(base64);
    } catch {
        throw new ChatRootPublicationError(code, message);
    }
}

function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : 'unknown error';
}
